// Certified Materials — constants (ported + trimmed for the PO table slice).

import type { IExtractedDataField, OrderStatus } from '../-types';

/** Feature flag that switches the page between brand and supplier views. */
export const SUPPLIER_FEATURE_FLAG = 'certified-materials-supplier';

/** localStorage prefix for persisted DataTable preferences. */
export const TABLE_STORAGE_KEY = 'certified-materials';

export const DEFAULT_PAGE_SIZE = 20;
// Due dates are order-level; the backend default moved to `dueDate` too.
export const DEFAULT_SORT_BY = 'dueDate';
export const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'asc';

type StatusBadgeVariant =
  | 'success'
  | 'info'
  | 'destructive'
  | 'warning'
  | 'muted';

export const ORDER_STATUS_CONFIG: Record<
  OrderStatus,
  { labelKey: string; variant: StatusBadgeVariant }
> = {
  todo: { labelKey: 'status.todo', variant: 'muted' },
  in_progress: { labelKey: 'status.inProgress', variant: 'info' },
  completed: { labelKey: 'status.completed', variant: 'success' },
  in_review: { labelKey: 'status.inReview', variant: 'warning' },
};

// Ordered by workflow lifecycle (To Do → In Progress → In Review → Completed)
// for the status picker in the order details drawer — CMMM-330.
export const ORDER_STATUS_OPTIONS: OrderStatus[] = [
  'todo',
  'in_progress',
  'in_review',
  'completed',
];

export const FILE_TYPE_LABELS: Record<string, string> = {
  tc: 'TC',
  invoice: 'Invoice',
  shipment: 'Shipment',
  po: 'Purchase Order',
  packing_list: 'Packing List',
  bill_of_lading: 'Bill of Lading',
  fabric_certificate: 'Fabric Certificate',
};

// ── Import PO spreadsheet ────────────────────────────────────────────────────

/** CDN-relative path of the downloadable PO import template. */
export const PO_IMPORT_TEMPLATE_PATH =
  '/static/templates/Certified_Materials_PO_Import_Template_v2.3.xlsx';

/** Max upload size for the PO spreadsheet (50 MB). */
export const IMPORT_MAX_FILE_SIZE = 50 * 1024 * 1024;

/**
 * Backend caps bulk order deletion at 500 ids per request
 * (`bulkDeleteOrdersSchema.max(500)`). Guard client-side so selecting more
 * (e.g. select-all) surfaces a clear message instead of an opaque 400.
 */
export const MAX_BULK_DELETE = 500;

/**
 * Same cap on the bulk-update endpoint (`bulkUpdateOrdersSchema.max(500)`), and
 * reachable the same way: row selection survives paging, so ticking a full
 * 500-row page and then one more row on the next page exceeds it.
 */
export const MAX_BULK_UPDATE = 500;

/** Accepted spreadsheet types — extensions (browse) + MIME types (drag). */
export const IMPORT_ACCEPT =
  '.xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';

/** Human-readable accepted-types label. */
export const IMPORT_ACCEPT_LABEL = '.xlsx, .xls, .csv';

/** Upload-tray owner id for the import dialog's uploader instance. */
export const IMPORT_OWNER_ID = 'certified-materials-import-orders';

// ── Upload evidence documents ────────────────────────────────────────────────

/** Accepted evidence-document types — extensions (browse) + MIME types (drag). */
export const DOC_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp';

/** Human-readable accepted-types label. */
export const DOC_ACCEPT_LABEL = 'PDF, JPG, PNG, WEBP';

/** Upload-tray owner id for the upload-docs dialog's uploader instance. */
export const UPLOAD_DOCS_OWNER_ID = 'certified-materials-upload-docs';

// ── Review documents ─────────────────────────────────────────────────────────

/** Selectable document-type keys (label resolved via reviewDialog.docTypes.<key>). */
export const REVIEW_DOC_TYPE_KEYS = [
  'tc',
  'fabric_certificate',
  'invoice',
  'shipment',
  'po',
  'unknown',
] as const;

/** Extracted-data keys rendered with a date editor. */
/**
 * Fields edited as a textarea rather than a single-line input.
 *
 * These hold several lines of text: an address runs street, district, city,
 * region, postcode, country, and a certification body is usually its name plus
 * its own address. A one-line input showed a sliver of the value, so a user
 * correcting an extracted one could not see what they were editing.
 */
export const EXTRACTED_DATA_TEXTAREA_FIELDS = [
  'sellerAddress',
  'buyerAddress',
  'certificationBody',
] as const;

export const EXTRACTED_DATA_DATE_FIELDS = [
  'issueDate',
  'invoiceDate',
  'shipmentDate',
  'orderDate',
  'deliveryDate',
] as const;

const TC_EXTRACTED_DATA_FIELDS: IExtractedDataField[][] = [
  [
    {
      key: 'tcNumber',
      labelKey: 'reviewDialog.extractedData.tcNumber',
      defaultLabel: 'TC number',
    },
    {
      key: 'issueDate',
      labelKey: 'reviewDialog.extractedData.issueDate',
      defaultLabel: 'Issue date',
      type: 'date',
    },
  ],
  [
    {
      key: 'placeOfIssue',
      labelKey: 'reviewDialog.extractedData.placeOfIssue',
      defaultLabel: 'Place of issue',
    },
    {
      key: 'certificationBody',
      labelKey: 'reviewDialog.extractedData.certificationBody',
      defaultLabel: 'Certification Body',
    },
  ],
  [
    {
      key: 'seller',
      labelKey: 'reviewDialog.extractedData.sellerName',
      defaultLabel: 'Seller name',
    },
    {
      key: 'buyer',
      labelKey: 'reviewDialog.extractedData.buyerName',
      defaultLabel: 'Buyer name',
    },
  ],
  [
    {
      key: 'sellerAddress',
      labelKey: 'reviewDialog.extractedData.sellerAddress',
      defaultLabel: 'Seller address',
    },
    {
      key: 'buyerAddress',
      labelKey: 'reviewDialog.extractedData.buyerAddress',
      defaultLabel: 'Buyer address',
    },
  ],
  [
    {
      key: 'sellerTeId',
      labelKey: 'reviewDialog.extractedData.sellerTeId',
      defaultLabel: 'Seller TE ID',
    },
    {
      key: 'buyerTeId',
      labelKey: 'reviewDialog.extractedData.buyerTeId',
      defaultLabel: 'Buyer TE ID',
    },
  ],
  [
    {
      key: 'grossWeight',
      labelKey: 'reviewDialog.extractedData.grossWeight',
      defaultLabel: 'Gross weight',
    },
    {
      key: 'certifiedWeight',
      labelKey: 'reviewDialog.extractedData.certifiedWeight',
      defaultLabel: 'Certified weight',
    },
  ],
  [
    {
      key: 'inputReferences',
      labelKey: 'reviewDialog.extractedData.inputReferences',
      defaultLabel: 'Input references',
    },
  ],
];

const INVOICE_EXTRACTED_DATA_FIELDS: IExtractedDataField[][] = [
  [
    {
      key: 'invoiceNumber',
      labelKey: 'reviewDialog.extractedData.invoiceNumber',
      defaultLabel: 'Invoice number',
    },
    {
      key: 'invoiceDate',
      labelKey: 'reviewDialog.extractedData.invoiceDate',
      defaultLabel: 'Invoice date',
      type: 'date',
    },
  ],
  [
    {
      key: 'buyer',
      labelKey: 'reviewDialog.extractedData.buyerName',
      defaultLabel: 'Buyer name',
    },
    {
      key: 'seller',
      labelKey: 'reviewDialog.extractedData.sellerName',
      defaultLabel: 'Seller name',
    },
  ],
  [
    {
      key: 'buyerAddress',
      labelKey: 'reviewDialog.extractedData.buyerAddress',
      defaultLabel: 'Buyer address',
    },
    {
      key: 'sellerAddress',
      labelKey: 'reviewDialog.extractedData.sellerAddress',
      defaultLabel: 'Seller address',
    },
  ],
  [
    {
      key: 'poReference',
      labelKey: 'reviewDialog.extractedData.poReference',
      defaultLabel: 'PO reference',
    },
  ],
];

const SHIPMENT_EXTRACTED_DATA_FIELDS: IExtractedDataField[][] = [
  [
    {
      key: 'shippingDocNumber',
      labelKey: 'reviewDialog.extractedData.shippingDocNumber',
      defaultLabel: 'Shipping doc number',
    },
    {
      key: 'shipmentDate',
      labelKey: 'reviewDialog.extractedData.shipmentDate',
      defaultLabel: 'Shipment date',
      type: 'date',
    },
  ],
  [
    {
      key: 'shipmentReference',
      labelKey: 'reviewDialog.extractedData.shipmentReference',
      defaultLabel: 'Shipment reference',
    },
  ],
];

/** Extracted-data field layout per document type. */
export const DOCUMENT_TYPE_EXTRACTED_FIELDS: Record<
  string,
  IExtractedDataField[][]
> = {
  tc: TC_EXTRACTED_DATA_FIELDS,
  fabric_certificate: [],
  invoice: INVOICE_EXTRACTED_DATA_FIELDS,
  shipment: SHIPMENT_EXTRACTED_DATA_FIELDS,
  po: [],
  packing_list: SHIPMENT_EXTRACTED_DATA_FIELDS,
  bill_of_lading: SHIPMENT_EXTRACTED_DATA_FIELDS,
  unknown: [],
};

/** Document types that have expandable sub-tables. */
export const EXPANDABLE_DOC_TYPES = [
  'tc',
  'invoice',
  'shipment',
  'packing_list',
  'bill_of_lading',
];
